import { DateTime } from '@ksyos/date-time';
import { connect } from 'react-redux';
import type { IBreadcrumbItem } from '@frontend/components/elements/common';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import type { IGroup } from '@frontend/components/interfaces/group';
import type { IOrganization } from '@frontend/components/interfaces/organization';
import type { ISpecialism } from '@frontend/components/interfaces/specialism';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import { Profession } from '@common/enums/profession';
import { ValidationErrorCode } from '@common/enums/validationErrorCode';
import { getDisplayName } from '@common/utils/getDisplayName';
import { employmentAddEditSchema } from '@common/validation/employment';
import {
    CancelButton,
    DangerAlertWhenErrorCode,
    DateInput,
    Dropdown,
    EnumDropdown,
    FormContainer,
    FormGroup,
    FormHeader,
    FormLabel,
    Header,
    InputCheckbox,
    InputGroup,
    LoadingTwo,
    NullableInputText,
    OverlapMessage,
    ReadOnlyInputText,
    SaveSubmit,
} from '@frontend/components/elements/common';
import { Component } from '@frontend/components/elements/Component';
import { HorizontalFormGroup } from '@frontend/components/elements/forms/HorizontalFormGroup';
import { InputContainer } from '@frontend/components/elements/forms/InputContainer';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { OrganizationSelection } from '@frontend/components/elements/OrganizationSelection';
import { ReprocessInfoModal } from '@frontend/components/elements/ReprocessInfoModal';
import { FixedWidthTemplate } from '@frontend/components/templates/FixedWidthTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { getSpecialismSelectOptions } from '@frontend/utils/getSpecialismSelectOptions';
import { OverlapChecker } from '@frontend/utils/overlapChecker';
import { RateLimiter } from '@frontend/utils/rateLimiter';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';

type IEmploymentsAddEditProps = IWithRouterProps;

interface IPractitioner {
    id?: string;
    identifierDeclaration: string | null;
    nameFamily: string;
}

interface IEmploymentsAddEditReduxProps {
    selectedEmploymentId: string;
}

interface IEmployment {
    emailAddressPayouts: string | null;
    emergencyNumber: string | null;
    endDate: string | null;
    groupManagerId: string | null;
    id?: string;
    identifierDeclarationPractitionerId: string | null;
    organizationId?: string;
    practitionerId: string | null;
    profession: Profession | null;
    relationNumber: string | null;
    specialismCode: string | null;
    startDate: string | null;
    zorgmailNummer: string | null;
}

interface IEmploymentsAddEditState {
    employment?: IEmployment;
    errorCode?: string;
    errors: ValidationErrors;
    groups?: IGroup[];
    initialRelationNumber: string | null;
    isSaving: boolean;
    organization?: IOrganization;
    overlap?: {
        endDate: string | null;
        id: string;
        startDate: string;
    };
    practitioner?: IPractitioner;
    practitionerUsedForIdentifierDeclaration?: IPractitioner;
    showReprocessInfo: boolean;
    specialisms: ISpecialism[];
    usesIdentifierDeclarationPractitionerId: boolean;
}

const mapStateToProps = ({ authentication }: IReduxState): IEmploymentsAddEditReduxProps => ({
    selectedEmploymentId: authentication.selectedEmploymentId,
});

export class InnerEmploymentsAddEdit extends Component<
    IEmploymentsAddEditProps & IEmploymentsAddEditReduxProps,
    IEmploymentsAddEditState
> {
    private readonly atMostOncePerSecond = new RateLimiter(1000);
    private readonly overlapChecker = new OverlapChecker(
        `/api/employments/${this.props.router.searchParams.getOptional('practitionerId')}/organizations`
    );

    constructor(props: IEmploymentsAddEditProps & IEmploymentsAddEditReduxProps, context: any) {
        super(props, context);

        assertHasAuthorization(ManagerAuthorization.employmentsOld);

        this.state = {
            errors: emptyValidationErrors,
            initialRelationNumber: null,
            isSaving: false,
            showReprocessInfo: false,
            specialisms: [],
            usesIdentifierDeclarationPractitionerId: false,
        };
    }

    public async componentDidMount() {
        this.setupComponent(this.props.router.params.employmentId);
    }

    public async componentDidUpdate(prevProps: IEmploymentsAddEditProps & IEmploymentsAddEditReduxProps) {
        if (this.props.router.params.employmentId !== prevProps.router.params.employmentId) {
            this.checkOverlap();
        }
    }

    public render() {
        const breadcrumbs: IBreadcrumbItem[] = [{ text: translateKey('employment.employment') }];
        if (this.props.router.params.employmentId) {
            breadcrumbs.push({ text: translateKey('common.edit') });
        } else {
            breadcrumbs.push({ text: translateKey('common.new') });
        }

        if (this.state.practitioner) {
            breadcrumbs.push({
                text: getDisplayName(this.state.practitioner),
                url: `/practitioners/${this.state.practitioner.id}`,
            });
        }

        const headerText =
            `${this.props.router.params.employmentId ? translateKey('common.edit') : translateKey('common.new')} ` +
            `${translateKey('employment.employments')} ${
                this.state.practitioner ? getDisplayName(this.state.practitioner) : ''
            } ` +
            `${this.state.organization && this.state.organization.name ? this.state.organization.name : ''}`;

        return (
            <FixedWidthTemplate breadcrumb={breadcrumbs}>
                <Header content={headerText} />

                <DangerAlertWhenErrorCode errorCode={this.state.errorCode} />

                <LoadingTwo
                    item1={this.state.employment}
                    item2={this.state.practitioner}
                    loaded={(employment, practitioner) => (
                        <FormContainer onSubmit={async () => await this.save(true)}>
                            {this.state.overlap && (
                                <OverlapMessage
                                    endDate={this.state.overlap.endDate}
                                    link={
                                        `/employments/${this.state.overlap.id}?` +
                                        `organizationId=${employment.organizationId}` +
                                        `&practitionerId=${practitioner.id}`
                                    }
                                    message={translateKey('employment.overlapMessage')}
                                    startDate={this.state.overlap.startDate}
                                />
                            )}

                            <FormGroup hasErrors={this.state.errors.has('startDate')}>
                                <FormLabel isRequired>{translateKey('common.startDate')}</FormLabel>
                                <InputGroup>
                                    <DateInput
                                        error={this.state.errors.get('startDate')}
                                        onChange={async (value) => {
                                            await this.handleChange('startDate', value);
                                            await this.checkOverlap();
                                        }}
                                        onDoValidate={async () => await this.validateOne('startDate')}
                                        value={employment.startDate}
                                    />
                                </InputGroup>
                            </FormGroup>

                            <FormGroup hasErrors={this.state.errors.has('endDate')}>
                                <FormLabel>{translateKey('common.endDate')}</FormLabel>
                                <InputGroup>
                                    <DateInput
                                        error={this.state.errors.get('endDate')}
                                        onChange={async (value) => {
                                            await this.handleChange('endDate', value);
                                            await this.checkOverlap();
                                        }}
                                        onDoValidate={async () => await this.validateOne('endDate')}
                                        value={employment.endDate}
                                    />
                                </InputGroup>
                            </FormGroup>

                            <>
                                <FormGroup hasErrors={this.state.errors.has('usesIdentifierDeclarationPractitionerId')}>
                                    <FormLabel>
                                        {translateKey(
                                            'identifierDeclarationOrRegistration.usesIdentifierDeclarationPractitionerId'
                                        )}
                                    </FormLabel>
                                    <InputGroup>
                                        <InputCheckbox
                                            checked={this.state.usesIdentifierDeclarationPractitionerId}
                                            error={this.state.errors.get('usesIdentifierDeclarationPractitionerId')}
                                            onChange={async (value) =>
                                                this.usesIdentifierDeclarationPractitionerIdHandleChange(value)
                                            }
                                        />
                                    </InputGroup>
                                </FormGroup>

                                {this.state.usesIdentifierDeclarationPractitionerId && (
                                    <HorizontalFormGroup>
                                        <FormLabel>
                                            {translateKey(
                                                'identifierDeclarationOrRegistration.identifierDeclarationPractitionerId'
                                            )}
                                        </FormLabel>
                                        <InputContainer>
                                            <ReadOnlyInputText
                                                placeholder={translateKey(
                                                    'identifierDeclarationOrRegistration.identifierDeclarationPractitionerId'
                                                )}
                                                value={
                                                    this.state.practitionerUsedForIdentifierDeclaration
                                                        ? `${getDisplayName(
                                                              this.state.practitionerUsedForIdentifierDeclaration
                                                          )} - ${
                                                              this.state.practitionerUsedForIdentifierDeclaration
                                                                  .identifierDeclaration
                                                          }`
                                                        : null
                                                }
                                            />
                                        </InputContainer>
                                    </HorizontalFormGroup>
                                )}
                            </>

                            {!this.props.router.params.employmentId && (
                                <>
                                    <FormHeader>{translateKey('organization.organization')}</FormHeader>

                                    <FormGroup hasErrors={this.state.errors.has('organizationId')}>
                                        <FormLabel isRequired>{translateKey('organization.organization')}</FormLabel>
                                        <InputGroup>
                                            <OrganizationSelection
                                                error={this.state.errors.get('organizationId')}
                                                onChange={async (value) => {
                                                    await this.handleChange('organizationId', value);
                                                    await this.checkOverlap();
                                                    await this.handleChange(
                                                        'identifierDeclarationPractitionerId',
                                                        null
                                                    );
                                                }}
                                                onDoValidate={async () => await this.validateOne('organizationId')}
                                                value={employment.organizationId}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                </>
                            )}

                            <FormGroup>
                                <FormLabel>{translateKey('employment.profession')}</FormLabel>
                                <InputGroup>
                                    <EnumDropdown
                                        enumName="profession"
                                        error={this.state.errors.get('profession')}
                                        jsEnum={Profession}
                                        onChange={async (value) => await this.handleChange('profession', value)}
                                        onDoValidate={async () => await this.validateOne('profession')}
                                        placeholder={translateKey('employment.profession')}
                                        value={employment.profession}
                                    />
                                </InputGroup>
                            </FormGroup>

                            {employment.profession === Profession.medicalSpecialist &&
                                this.state.specialisms.length > 0 && (
                                    <FormGroup hasErrors={this.state.errors.has('specialismCode')}>
                                        <FormLabel isRequired={true}>{translateKey('employment.specialism')}</FormLabel>
                                        <InputGroup>
                                            <Dropdown
                                                error={this.state.errors.get('specialismCode')}
                                                onChange={async (value) =>
                                                    await this.handleChange('specialismCode', value)
                                                }
                                                onDoValidate={async () => await this.validateOne('specialismCode')}
                                                selectOptions={getSpecialismSelectOptions(this.state.specialisms)}
                                                value={employment.specialismCode}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}

                            <FormGroup hasErrors={this.state.errors.has('relationNumber')}>
                                <FormLabel>{translateKey('organization.relationNumber')}</FormLabel>
                                <InputGroup>
                                    <NullableInputText
                                        error={this.state.errors.get('relationNumber')}
                                        onChange={async (value) => await this.handleChange('relationNumber', value)}
                                        onDoValidate={async () => await this.validateOne('relationNumber')}
                                        placeholder={translateKey('organization.relationNumber')}
                                        value={employment.relationNumber || null}
                                    />
                                </InputGroup>
                            </FormGroup>

                            <FormGroup hasErrors={this.state.errors.has('emailAddressPayouts')}>
                                <FormLabel>{translateKey('employment.emailAddressPayouts')}</FormLabel>
                                <InputGroup>
                                    <NullableInputText
                                        error={this.state.errors.get('emailAddressPayouts')}
                                        onChange={async (value) =>
                                            await this.handleChange('emailAddressPayouts', value)
                                        }
                                        onDoValidate={async () => await this.validateOne('emailAddressPayouts')}
                                        placeholder={translateKey('employment.emailAddressPayouts')}
                                        value={employment.emailAddressPayouts || null}
                                    />
                                </InputGroup>
                            </FormGroup>

                            <FormGroup hasErrors={this.state.errors.has('emergencyNumber')}>
                                <FormLabel>{translateKey('patient.emergencyNumber')}</FormLabel>
                                <InputGroup>
                                    <NullableInputText
                                        error={this.state.errors.get('emergencyNumber')}
                                        onChange={async (value) => await this.handleChange('emergencyNumber', value)}
                                        onDoValidate={async () => await this.validateOne('emergencyNumber')}
                                        placeholder={translateKey('patient.emergencyNumber')}
                                        value={employment.emergencyNumber || null}
                                    />
                                </InputGroup>
                            </FormGroup>

                            <FormHeader>{translateKey('employment.zorgmail')}</FormHeader>

                            <FormGroup hasErrors={this.state.errors.has('zorgmailNummer')}>
                                <FormLabel>{translateKey('employment.zorgmailNummer')}</FormLabel>
                                <InputGroup>
                                    <NullableInputText
                                        error={this.state.errors.get('zorgmailNummer')}
                                        onChange={async (value) => await this.handleChange('zorgmailNummer', value)}
                                        onDoValidate={async () => await this.validateOne('zorgmailNummer')}
                                        placeholder={translateKey('employment.zorgmailNummer')}
                                        value={employment.zorgmailNummer}
                                    />
                                </InputGroup>
                            </FormGroup>

                            <FormHeader>{translateKey('employment.groupManager')}</FormHeader>

                            {this.state.groups && (
                                <FormGroup hasErrors={this.state.errors.has('groupManagerId')}>
                                    <FormLabel>{translateKey('employment.groupManager')}</FormLabel>
                                    <InputGroup>
                                        <Dropdown
                                            error={this.state.errors.get('groupManagerId')}
                                            onChange={async (value) => await this.handleChange('groupManagerId', value)}
                                            onDoValidate={async () => await this.validateOne('groupManagerId')}
                                            selectOptions={[{ value: '', text: translateKey('common.select') }].concat(
                                                this.state.groups.map((groupManager) => ({
                                                    value: groupManager.id,
                                                    text: groupManager.name || '',
                                                }))
                                            )}
                                            value={employment.groupManagerId}
                                        />
                                    </InputGroup>
                                </FormGroup>
                            )}

                            {this.state.showReprocessInfo && (
                                <ReprocessInfoModal
                                    onNo={async () => await this.setState({ showReprocessInfo: false })}
                                    onYes={async () => await this.save(false)}
                                />
                            )}
                            <CancelButton
                                disabled={this.state.isSaving}
                                to={this.returnTo()}
                            />
                            <SaveSubmit disabled={this.state.isSaving || !!this.state.overlap} />
                        </FormContainer>
                    )}
                />
            </FixedWidthTemplate>
        );
    }

    private returnTo() {
        const returnTo = this.props.router.searchParams.get('returnTo');
        if (returnTo === 'organization') {
            return `/organizations/${this.props.router.searchParams.get('organizationId')}`;
        }

        return `/practitioners/${this.props.router.searchParams.get('practitionerId')}`;
    }

    private async handleChange<K extends keyof IEmployment>(name: K, value: IEmployment[K]) {
        await this.setState(({ employment }) => ({ employment: { ...employment, [name]: value } }));

        if (
            name === 'profession' &&
            this.state.employment?.profession === Profession.medicalSpecialist &&
            this.state.specialisms.length === 0
        ) {
            await this.loadSpecialisms();
        }

        if (name === 'organizationId') {
            if (value) {
                await this.loadOrganization();
            } else {
                await this.setState({ organization: undefined });
            }
        } else if (name === 'profession') {
            if (this.state.employment?.profession !== Profession.medicalSpecialist) {
                await this.setState(({ employment }) => ({ employment: { ...employment, specialismCode: null } }));
            }
        }
    }

    private async validateOne(name: keyof IEmployment) {
        await this.setState(({ employment, errors }) => ({
            errors: updateValidationErrorsForOne(employmentAddEditSchema, employment, errors, name),
        }));
    }

    private async loadEmployment(employmentId?: string) {
        const url = `/api/employments/${employmentId ? employmentId : this.props.router.params.employmentId}`;

        const result = await fetchJson(url, 'GET');
        const usesIdentifierDeclarationPractitionerId = result.json.identifierDeclarationPractitionerId !== null;
        await this.setState({
            employment: result.json,
            usesIdentifierDeclarationPractitionerId: usesIdentifierDeclarationPractitionerId,
            initialRelationNumber: result.json.relationNumber || null,
        });
    }

    private async loadPractitioner() {
        if (!this.state.employment || !this.state.employment.practitionerId) {
            return;
        }
        const url = `/api/practitioners/${this.state.employment.practitionerId}`;

        const result = await fetchJson(url, 'GET');
        await this.setState({ practitioner: result.json });
    }

    private async loadOrganization() {
        if (!this.state.employment || !this.state.employment.organizationId) {
            return;
        }

        const url = `/api/organizations/${this.state.employment.organizationId}`;

        const result = await fetchJson(url, 'GET');
        await this.setState({ organization: result.json });
    }

    private async loadGroups() {
        const url = '/api/groups';

        const result = await fetchJson(url, 'GET');
        await this.setState({ groups: result.json.items });
    }

    private async save(checkRelationNumber: boolean) {
        const { errors: validationErrors, item } = validate(employmentAddEditSchema, this.state.employment);
        await this.setState({ errors: validationErrors });
        if (validationErrors.size > 0) {
            return;
        }

        if (
            checkRelationNumber &&
            this.state.initialRelationNumber === null &&
            this.state.initialRelationNumber !== this.state.employment?.relationNumber
        ) {
            await this.setState({ showReprocessInfo: true });

            return;
        }

        let url = '/api/employments/';

        if (this.props.router.params.employmentId) {
            url += this.props.router.params.employmentId;
        }

        await this.setState({ isSaving: true });

        const result = await fetchJson(url, 'POST', item, [ValidationErrorCode.NO_AGB_CODE]);
        if (result.errorCode) {
            if (result.errorCode === ValidationErrorCode.NO_AGB_CODE) {
                await this.setState(({ errors }) => {
                    return {
                        isSaving: false,
                        errors: errors.set(
                            'identifierDeclarationPractitionerId',
                            'identifierDeclarationOrReferenceRequired'
                        ),
                    };
                });
            } else {
                await this.setState({ isSaving: false, errorCode: result.errorCode });
            }
        } else {
            this.props.router.navigate(this.returnTo());
        }
    }

    private async checkOverlap() {
        const practitionerId = this.getPractitionerId();
        const organizationId = this.getOrganizationId();
        if (practitionerId) {
            this.atMostOncePerSecond.callMeMaybe(async () => {
                if (this.state.employment && this.state.employment.organizationId && this.state.employment.startDate) {
                    const overlap = await this.overlapChecker.check(
                        { organizationId: organizationId },
                        this.state.employment.startDate,
                        this.state.employment.endDate,
                        this.props.router.params.employmentId
                    );
                    await this.setState({ overlap });
                }
            });
        }
    }

    private getPractitionerId() {
        return this.state.practitioner && this.state.practitioner.id;
    }

    private getOrganizationId() {
        return this.state.employment && this.state.employment.organizationId;
    }

    private async setupComponent(employmentId?: string) {
        if (employmentId) {
            await this.loadEmployment(
                this.props.router.params.employmentId ? this.props.router.params.employmentId : employmentId
            );
        } else {
            await this.setState({
                employment: {
                    organizationId: this.props.router.searchParams.get('organizationId') ?? undefined,
                    practitionerId: this.props.router.searchParams.get('practitionerId'),
                    startDate: DateTime.now().format('YYYY-MM-DD'),
                    endDate: null,
                    zorgmailNummer: null,
                    groupManagerId: this.props.router.searchParams.get('groupManagerId'),
                    relationNumber: null,
                    emailAddressPayouts: null,
                    identifierDeclarationPractitionerId: null,
                    emergencyNumber: null,
                    profession: null,
                    specialismCode: null,
                },
                usesIdentifierDeclarationPractitionerId: false,
                initialRelationNumber: null,
            });
        }
        await this.loadPractitioner();
        await this.loadOrganization();
        await this.loadGroups();
        await this.loadPractitionerUsedForIdentifierDeclaration();

        if (this.state.employment?.profession === Profession.medicalSpecialist) {
            await this.loadSpecialisms();
        }
    }

    private async usesIdentifierDeclarationPractitionerIdHandleChange(value: boolean) {
        if (!value) {
            await this.handleChange('identifierDeclarationPractitionerId', null);
        }

        await this.setState({ usesIdentifierDeclarationPractitionerId: value });

        // Re-validate values that depend on usesIdentifierDeclarationPractitionerId
        await this.validateOne('identifierDeclarationPractitionerId');
    }

    private async loadPractitionerUsedForIdentifierDeclaration() {
        if (this.state.employment?.identifierDeclarationPractitionerId) {
            const result = await fetchJson(
                `/api/practitioners/${this.state.employment.identifierDeclarationPractitionerId}`,
                'GET'
            );
            await this.setState({ practitionerUsedForIdentifierDeclaration: result.json });
        }
    }

    private async loadSpecialisms() {
        const result = await fetchJson('/api/dhdThesaurus/specialisms', 'GET');
        await this.setState({ specialisms: result.json.items.filter((item: ISpecialism) => item.isActive) });
    }
}

export const EmploymentsAddEdit = connect(mapStateToProps)(withRouter(InnerEmploymentsAddEdit));
